import {
  Cpu, Calendar, Activity, Calculator,
  Shield, MessageCircle, Package,
} from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

const features = [
  { icon: <Cpu className="w-5 h-5" />, title: 'AI Repair Assistant', desc: 'Error codes, voltages, part numbers — real answers, not scripts.' },
  { icon: <Calendar className="w-5 h-5" />, title: 'PM Calendar', desc: 'Auto-generated from your inventory. Supplies, tasks, reminders.' },
  { icon: <Activity className="w-5 h-5" />, title: 'Equipment Dashboard', desc: 'Every machine, one view. Track status, photos, repairs over time.' },
  { icon: <Calculator className="w-5 h-5" />, title: 'Replace vs. Repair', desc: 'Repair cost, market value, install, disposal — all factored.' },
  { icon: <Shield className="w-5 h-5" />, title: 'Manufacturer Playbook', desc: 'Life Fitness, Precor, Technogym, Matrix — how to get what you need.' },
  { icon: <Package className="w-5 h-5" />, title: 'Parts & Supplies', desc: 'Auto-generated parts stocklist based on your equipment. Never caught without the right part.' },
  { icon: <MessageCircle className="w-5 h-5" />, title: 'Async Support', desc: 'Photo or video of the issue. Same-day response from a real expert.' },
];

export default function FeatureSection() {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <section className="py-10 md:py-14 px-4" id="features" ref={ref}>
      <div className={`max-w-5xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="grid md:grid-cols-5 gap-8 items-start">
          {/* Left: big statement */}
          <div className="md:col-span-2">
            <p className="text-blue-400 text-xs font-semibold tracking-wider uppercase mb-2">
              Iron Tier
            </p>
            <h2 className="text-xl md:text-3xl font-extrabold mb-3 leading-tight">
              Everything you need to run your equipment yourself.
            </h2>
            <p className="text-white/50 text-sm leading-relaxed">
              No more waiting on hold. No more $400+ service calls. Just you, with the right tools and knowledge.
            </p>
            <p className="text-white/30 text-xs mt-4">
              Powered by <a href="https://athnxtech.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 font-semibold hover:underline">AthnxTech</a> — the same AI used by professional repair techs.
            </p>
          </div>

          {/* Right: compact feature list */}
          <div className="md:col-span-3 space-y-2">
            {features.map((feature, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3 md:p-4 rounded-xl border border-white/5 bg-white/[0.03] hover:bg-white/[0.06] hover:border-blue-500/20 transition-all duration-200 hover:translate-x-1"
              >
                <div className="text-blue-400 mt-0.5 flex-shrink-0">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-sm font-bold mb-0.5">{feature.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
